/**
 * Downloads the sidebar pages' code after login, while the user is still on
 * the first page. A click then switches immediately instead of waiting for
 * that page's file.
 */

type PageLoader = () => Promise<unknown>;

// ============================================================
// Private methods
// ============================================================

/** Same import paths as Router.tsx, so the browser caches one copy of each page. */
const SIDEBAR_PAGE_LOADERS: readonly PageLoader[] = [
  () => import('../pages/AiChatPage'),
  () => import('../pages/ApplicationRoomsList'),
  () => import('../pages/ResponseListNew'),
  () => import('../pages/ConversationsNew'),
  () => import('../pages/ReportingNew'),
  () => import('../pages/AiGuidanceList'),
  () => import('../pages/ChannelList'),
  () => import('../pages/KnowledgeSourceList'),
  () => import('../pages/KnowledgeSourceConfigurationsList'),
  () => import('../pages/IntegrationsList'),
  () => import('../pages/Users'),
];

/**
 * Runs `run` once the browser is idle, or after a second if it never is.
 *
 * Example: scheduleWhenIdle(() => load()) starts the loads off the first paint.
 */
function scheduleWhenIdle(run: () => void): void {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => run(), { timeout: 2000 });
    return;
  }
  setTimeout(run, 1000);
}

// ============================================================
// Public methods
// ============================================================

/**
 * Loads every sidebar page in parallel. One failed file does not stop the rest.
 *
 * Example: prefetchSidebarPages([() => import('./Foo')]) resolves after Foo loads.
 */
export function prefetchSidebarPages(
  loaders: readonly PageLoader[] = SIDEBAR_PAGE_LOADERS,
  schedule: (run: () => void) => void = scheduleWhenIdle
): Promise<void> {
  return new Promise(resolve => {
    schedule(() => {
      void Promise.all(loaders.map(load => load().catch(() => undefined))).then(() => resolve());
    });
  });
}
